import { authProtectedRoutes, type RoutePathType } from '@app/index';
import { API_METHODS } from '@compass/client/constants/api-methods';
import { AvatarWithStatus } from '@components/AvatarWithStatus';
import { OrgWideCasesToggle } from '@components/OrgWideCasesToggle';
import { ReactScanToggle } from '@components/ReactScanToggle';
import { RoleBadge } from '@components/RoleBadge';
import { TestModeBanner } from '@components/TestModeBanner';
import { Separator } from '@components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  useSidebar,
} from '@components/ui/sidebar';
import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath } from '@utils/routes';
import { BarChart3, ScanEye, Settings as SettingsIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { isMyTasksEnabledForOrg } from '@/constants/feature-flags';
import { useMyOpenTaskCount } from '@/hooks/useAllMyTasks';
import { useIs100msUser } from '@/hooks/useIs100msUser';
import { useIsAiChatAllowedEmail } from '@/hooks/useIsAiChatAllowedEmail';
import { useAPIPermission, useCurrentUserContextRoles } from '@/hooks/usePermissions';
import { useShouldShowAllTasks } from '@/hooks/useShouldShowAllTasks';
import { useShouldShowAnalyticsPage } from '@/hooks/useShouldShowAnalyticsPage';
import { useShouldShowCallsPage } from '@/hooks/useShouldShowCallsPage';
import { useShouldShowCampaignsPage } from '@/hooks/useShouldShowCampaignsPage';
import { useShouldShowCasesAnalyticsPage } from '@/hooks/useShouldShowCasesAnalyticsPage';
import { useShouldShowMyCasesPage } from '@/hooks/useShouldShowMyCasesPage';
import { useShouldShowQcPage } from '@/hooks/useShouldShowQcPage';
import { useShouldShowWorkforceConsole } from '@/hooks/useShouldShowWorkforceConsole';
import { useCurrentUser } from '@/hooks/useUsers';
import { useUserTier } from '@/hooks/useUserTier';
import { isReactScanAvailable } from '@/reactScanInit';

interface AuthSidebarProps {
  children?: React.ReactNode;
}

// Match a pathCategory on whole path segments, so '/cases' does not light up
// on '/org-…/cases-analytics'.
const isCategoryActive = (path: string, category: string): boolean =>
  path.includes(`${category}/`) || path.endsWith(category);

// Renders a sidebar item for a route that is org-scoped (no :progId), e.g. QC.
function OrgScopedSidebarItem({
  route,
  path,
  organizationId,
}: {
  route: RoutePathType;
  path: string;
  organizationId: string;
}) {
  const active = path.includes(route.pathCategory);
  const Icon = active && route.solidIcon ? route.solidIcon : route.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active}>
        <Link to={buildOrgRoutePath(route.path, organizationId)} title={route.title}>
          {Icon && <Icon />}
          <span>{route.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// One "Analytics" entry for the two analytics pages (QC and AE / PQC). Each page is listed
// beneath it when its own access hook allows; the entry links to the first one listed.
function AnalyticsSidebarGroup({
  path,
  organizationId,
}: {
  path: string;
  organizationId: string;
}): React.ReactNode {
  const { shouldShow: shouldShowAnalyticsPage } = useShouldShowAnalyticsPage();
  const { shouldShow: shouldShowCasesAnalyticsPage } = useShouldShowCasesAnalyticsPage();

  const pages = [
    shouldShowAnalyticsPage && authProtectedRoutes.find(r => r.pathCategory === '/analytics'),
    shouldShowCasesAnalyticsPage &&
      authProtectedRoutes.find(r => r.pathCategory === '/cases-analytics'),
  ].filter((r): r is RoutePathType => !!r);
  if (pages.length === 0) return null;

  // '/cases-analytics' does not contain '/analytics' (the hyphen), so check both.
  const active = isCategoryActive(path, '/analytics') || isCategoryActive(path, '/cases-analytics');

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active}>
        <Link to={buildOrgRoutePath(pages[0].path, organizationId)} title="Analytics beta">
          <BarChart3 />
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">Analytics</span>
            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary group-data-[collapsible=icon]:hidden">
              Beta
            </span>
          </span>
        </Link>
      </SidebarMenuButton>
      <SidebarMenuSub>
        {pages.map(route => (
          <SidebarMenuSubItem key={route.path}>
            <SidebarMenuSubButton asChild isActive={isCategoryActive(path, route.pathCategory)}>
              <Link to={buildOrgRoutePath(route.path, organizationId)}>
                <span className="truncate">{route.title}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        ))}
      </SidebarMenuSub>
    </SidebarMenuItem>
  );
}

// Inner component that can use useSidebar hook
function SidebarContentComponent() {
  const location = useLocation();
  const path = location.pathname;
  const { selectedProgramId, selectedOrganizationId, reactScanEnabled, setReactScanEnabled } =
    useStore();
  // react-scan is a dev-only profiler (see src/reactScanInit.ts); the toggle only
  // renders in development builds, never in production.
  const reactScanAvailable = isReactScanAvailable;
  const { data: userData } = useCurrentUser(
    selectedOrganizationId ?? undefined,
    selectedProgramId ?? undefined,
  );
  // Cases needs case_record read (program/assigned). The analytics-only role
  // (qc-analytics-viewer) has neither, so hide the Cases nav for it.
  const { hasPermission: canViewCases } = useAPIPermission(
    API_METHODS.CaseRecordsService.FindCaseRecords,
  );
  const { shouldShow: shouldShowCallsPage } = useShouldShowCallsPage();
  const shouldShowCampaignsPage = useShouldShowCampaignsPage();
  const shouldShowMyCasesPage = useShouldShowMyCasesPage();
  const { roles } = useCurrentUserContextRoles();
  const primaryRole = roles?.[0];
  const currentUser = userData?.user;
  const { shouldShow: shouldShowQcPage } = useShouldShowQcPage();
  // All Tasks: same audience as the Workforce Console under its own mode flag, only for
  // orgs in REACT_APP_ALL_TASKS_ENABLED_ORG_IDS (same hook as the route guard).
  const { shouldShow: shouldShowAllTasks } = useShouldShowAllTasks();
  // Tier-2 users have an org-scoped cases page; their "Cases" nav links to
  // /org-{id}/cases (no program), matching the route + redirect behavior.
  // `isEligible` reflects the raw permission tier (independent of the opt-in),
  // so the opt-in toggle only renders for users who can actually use it.
  const { tier, isEligible: isOrgWideCasesEligible } = useUserTier();
  const is100msUser = useIs100msUser();
  const isAiChatAllowedEmail = useIsAiChatAllowedEmail();
  const canShowAiChat = is100msUser || isAiChatAllowedEmail;

  // Workflows will be fetched by the pages that need them (e.g., CasesContainer)
  // QC is excluded here — it is rendered separately as the last nav item via QcSidebarItem.
  // Workforce Console, My Tasks and My Cases are also excluded so they can be rendered
  // immediately above the Settings footer (all are org-scoped, not program-scoped).
  const filteredRoutes = useMemo(() => {
    return authProtectedRoutes.filter(route => {
      if (!route.showInSidebar) return false;
      if (route.pathCategory === '/qc') return false;
      if (route.pathCategory === '/analytics') return false;
      if (route.pathCategory === '/cases-analytics') return false;
      if (route.pathCategory === '/workforce') return false;
      if (route.pathCategory === '/my-cases') return false;
      if (route.pathCategory === '/my-tasks') return false;

      // Hide Cases for users without case_record read (e.g. analytics-only role)
      if (route.pathCategory === '/cases' && !canViewCases) return false;

      // Conditionally show Calls tab only for programs with pqc_ingestion workflow
      if (route.pathCategory === '/calls' && !shouldShowCallsPage) return false;

      // Hide Campaigns without ListCampaigns permission (e.g. Case Manager) or for PQC workflow (Knipper)
      if (route.pathCategory === '/campaigns' && !shouldShowCampaignsPage) return false;

      // Hide AI Chat unless user is a 100ms user or in the allowlist env var
      if (route.pathCategory === '/chat' && !canShowAiChat) return false;

      if (route.pathCategory === '/all-tasks' && !shouldShowAllTasks) return false;

      return true;
    });
  }, [
    canViewCases,
    shouldShowCallsPage,
    shouldShowCampaignsPage,
    canShowAiChat,
    shouldShowAllTasks,
  ]);

  const workforceRoute = useMemo(
    () => authProtectedRoutes.find(r => r.pathCategory === '/workforce'),
    [],
  );
  // My Tasks sits directly above My Cases, and only for orgs in
  // REACT_APP_TASKS_ENABLED_ORG_IDS.
  const showMyTasks = isMyTasksEnabledForOrg(selectedOrganizationId);
  const myWorkRoutes = useMemo(
    () =>
      (showMyTasks ? ['/my-tasks', '/my-cases'] : ['/my-cases'])
        .map(category => authProtectedRoutes.find(r => r.pathCategory === category))
        .filter((r): r is RoutePathType => !!r),
    [showMyTasks],
  );
  const openTaskCount = useMyOpenTaskCount(showMyTasks && shouldShowMyCasesPage);
  // Workforce Console: team leads, or with `permissions_based_wc_access` on, the
  // roles the permission map allows, outside PQC programs and orgs hidden by
  // `hide_workforce_console_<org_id>` (same hook as the route guard).
  const { shouldShow: shouldShowWorkforceConsole } = useShouldShowWorkforceConsole();

  // Webhooks has no nav entry: it opens from Settings → Developer, so its
  // pages keep Settings highlighted.
  const settingsActive = path.includes('/settings') || path.includes('/webhooks');

  return (
    <>
      <SidebarContent className="py-2">
        {/* Navigation items — QC is always rendered last, do not reorder. */}
        <SidebarGroup>
          <SidebarMenu>
            {filteredRoutes.map(route => {
              // Check if current path matches the route category
              // For routes with org/prog context, check if path contains the category
              // For example: /org-xxx/prog-xxx/campaigns should match /campaigns
              const active = isCategoryActive(path, route.pathCategory);
              const Icon = active && route.solidIcon ? route.solidIcon : route.icon;
              // Tier-2 users' Cases page is org-scoped (no program in the URL), as is All Tasks.
              const isOrgScoped =
                (route.pathCategory === '/cases' && tier === 'tier2') ||
                route.pathCategory === '/all-tasks';
              const routePath = isOrgScoped
                ? buildOrgRoutePath(route.path, selectedOrganizationId || '')
                : buildRoutePath(route.path, selectedOrganizationId || '', selectedProgramId || '');
              return (
                <SidebarMenuItem key={route.path}>
                  <SidebarMenuButton asChild isActive={active}>
                    <Link to={routePath} title={route.title}>
                      {Icon && <Icon />}
                      <span>{route.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
            {/* Analytics (QC + AE / PQC) is org-scoped, rendered just above QC. */}
            <AnalyticsSidebarGroup path={path} organizationId={selectedOrganizationId || ''} />
            {/* QC is always the last nav item — do not move it above other routes. */}
            {shouldShowQcPage && (
              <OrgScopedSidebarItem
                route={authProtectedRoutes.find(r => r.pathCategory === '/qc')!}
                path={path}
                organizationId={selectedOrganizationId || ''}
              />
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <Separator />
        {isOrgWideCasesEligible && (
          <div className="overflow-hidden px-2 py-2 transition-all duration-200 ease-linear group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:py-0 group-data-[collapsible=icon]:opacity-0">
            <OrgWideCasesToggle className="w-full" />
          </div>
        )}
        {reactScanAvailable && (
          <div className="overflow-hidden px-2 pb-2 transition-all duration-200 ease-linear group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:py-0 group-data-[collapsible=icon]:opacity-0">
            <ReactScanToggle className="w-full" />
          </div>
        )}
        <SidebarMenu>
          {reactScanAvailable && (
            <SidebarMenuItem className="hidden group-data-[collapsible=icon]:block">
              <SidebarMenuButton onClick={() => setReactScanEnabled(!reactScanEnabled)}>
                <ScanEye className={reactScanEnabled ? 'text-orange-600' : ''} />
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* My Tasks and My Cases — org-scoped, visible to all org members, sit in the
              footer block. Hidden for PQC workflow (Knipper organization). */}
          {shouldShowMyCasesPage &&
            myWorkRoutes.map(route => {
              const count = route.pathCategory === '/my-tasks' ? openTaskCount : 0;
              return (
                <SidebarMenuItem key={route.pathCategory}>
                  <SidebarMenuButton asChild isActive={path.includes(route.pathCategory)}>
                    <Link
                      to={buildOrgRoutePath(route.path, selectedOrganizationId || '')}
                      title={count ? `${route.title} (${count} open)` : route.title}
                      className="relative"
                    >
                      {route.icon &&
                        React.createElement(
                          path.includes(route.pathCategory) && route.solidIcon
                            ? route.solidIcon
                            : route.icon,
                        )}
                      <span className="truncate">{route.title}</span>
                      {/* Open-task pill: after the label when expanded, on the icon's corner
                          when the sidebar is collapsed to icons. */}
                      {count > 0 && (
                        <span className="ml-auto shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 tabular-nums text-primary-foreground group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:right-0 group-data-[collapsible=icon]:top-0 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:text-[9px] group-data-[collapsible=icon]:leading-[14px]">
                          {count > 99 ? '99+' : count}
                          <span className="sr-only"> open</span>
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          {/* Workforce Console — org-scoped route, sits immediately above Settings. */}
          {workforceRoute && shouldShowWorkforceConsole && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={path.includes(workforceRoute.pathCategory)}>
                <Link
                  to={buildOrgRoutePath(workforceRoute.path, selectedOrganizationId || '')}
                  title={workforceRoute.title}
                >
                  {workforceRoute.icon &&
                    React.createElement(
                      path.includes(workforceRoute.pathCategory) && workforceRoute.solidIcon
                        ? workforceRoute.solidIcon
                        : workforceRoute.icon,
                    )}
                  <span>{workforceRoute.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={settingsActive}>
              <Link
                to={buildRoutePath(
                  '/settings',
                  selectedOrganizationId || '',
                  selectedProgramId || '',
                )}
              >
                <SettingsIcon />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <Separator />
        <div className="flex items-center gap-3 overflow-hidden px-2 pt-2 pb-3 transition-all duration-200 ease-linear group-data-[collapsible=icon]:px-0">
          <AvatarWithStatus
            name={currentUser?.name}
            email={currentUser?.email}
            userId={currentUser?.id}
            size="md"
            className="shrink-0"
          />
          <div className="flex flex-col items-start text-left flex-1 min-w-0 overflow-hidden transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:opacity-0">
            <span className="text-sm font-medium truncate w-full">{currentUser?.name}</span>
            {primaryRole && <RoleBadge role={primaryRole} size="sm" variant="muted" />}
          </div>
        </div>
      </SidebarFooter>

      <SidebarRail />
    </>
  );
}

// Wrapper component to handle hover functionality
function HoverSidebar() {
  const { setOpen, open } = useSidebar();

  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="fixed left-0 top-[50px] h-[calc(100vh-50px)] z-30 transition-[width] duration-200 ease-linear"
      style={{ width: open ? 'var(--sidebar-width)' : 'var(--sidebar-width-icon)' }}
    >
      <Sidebar collapsible="icon" variant="sidebar" className="h-full bg-background">
        <SidebarContentComponent />
      </Sidebar>
    </div>
  );
}

export function AuthSidebar({ children }: AuthSidebarProps) {
  return (
    <div className="h-[calc(100vh-50px)] flex">
      <SidebarProvider defaultOpen={false}>
        <HoverSidebar />
        {/* Spacer to reserve space for fixed sidebar */}
        <div className="w-12 shrink-0" />
        <div className="flex flex-col h-full overflow-hidden flex-1">
          <TestModeBanner />
          <div className="flex-1 overflow-auto">{children}</div>
        </div>
      </SidebarProvider>
    </div>
  );
}
